/**
 * The money rule, enforced once (TMMRSL-0031, H §6.3): reseller money is ALWAYS per currency and
 * NEVER summed across currencies. `PerCurrency` renders one row per entry — each amount through
 * `moneyIn(minor, row.currency)` — and has no total row: it cannot add two currencies.
 * Every reseller-wide money table (Dashboard, Revenue) renders through it; `moneyIn` is the one
 * formatter for a single amount beside its own currency. A column whose amount is absent (`null`) on
 * every row is hidden. A single absent amount shows the unavailable dash, never 0 (TMMRSL-0047).
 */
import type { ReactNode } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/i18n/useLocale';
import { moneyIn } from './moneyIn';

export interface MoneyColumn<T> {
  key: string;
  label: string;
  /** The row's amount, or `null` when tmm-api doesn't send it (shown as the dash). */
  minor: (row: T) => number | null;
}

export interface PerCurrencyProps<T extends { currency: string }> {
  /** One entry per row; each row's amounts are in ITS `currency`. */
  rows: readonly T[];
  columns: readonly MoneyColumn<T>[];
  /** Accessible name of the table. */
  label: string;
  /** A leading label column; by default the row's currency code. */
  rowHeader?: { label: string; render: (row: T) => ReactNode };
  /** Shown instead of the table when there are no rows. */
  empty: string;
}

export function PerCurrency<T extends { currency: string }>({
  rows,
  columns,
  label,
  rowHeader,
  empty,
}: PerCurrencyProps<T>) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('money.unavailable');
  const visible = columns.filter((c) => rows.some((row) => c.minor(row) !== null));

  if (rows.length === 0) {
    return (
      <Paper sx={{ p: 2.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {empty}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label={label}>
          <TableHead>
            <TableRow>
              <TableCell>{rowHeader ? rowHeader.label : t('money.currency')}</TableCell>
              {rowHeader ? <TableCell>{t('money.currency')}</TableCell> : null}
              {visible.map((column) => (
                <TableCell key={column.key} align="right">
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={`${row.currency}-${index}`}>
                <TableCell component="th" scope="row">
                  {rowHeader ? rowHeader.render(row) : row.currency || dash}
                </TableCell>
                {rowHeader ? <TableCell>{row.currency || dash}</TableCell> : null}
                {visible.map((column) => {
                  const minor = column.minor(row);
                  return (
                    <TableCell
                      key={column.key}
                      align="right"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {minor === null ? dash : moneyIn(minor, row.currency, locale, dash)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}
